import { getApp, getApps, initializeApp } from 'firebase/app';
import {
  GoogleAuthProvider,
  createUserWithEmailAndPassword,
  getAuth,
  getRedirectResult,
  onIdTokenChanged,
  sendEmailVerification,
  sendPasswordResetEmail,
  signInWithCustomToken,
  signInWithEmailAndPassword,
  signInWithRedirect,
  signOut,
} from 'firebase/auth';

import { getTenantRuntime } from '../../app/bootstrap/tenantRuntime';

function centralAuthConfig() {
  const centralAuth = getTenantRuntime()?.tenant?.centralAuth;
  if (!centralAuth?.apiKey || !centralAuth.authDomain || !centralAuth.projectId || !centralAuth.appId) {
    throw new Error('Workspace sign-in is not configured.');
  }
  return {
    apiKey: centralAuth.apiKey,
    authDomain: centralAuth.authDomain,
    projectId: centralAuth.projectId,
    appId: centralAuth.appId,
    messagingSenderId: centralAuth.messagingSenderId,
  };
}

function workspaceAuth() {
  const config = centralAuthConfig();
  const app = getApps().length ? getApp() : initializeApp(config);
  return getAuth(app);
}

export async function requestWorkspacePasswordReset(email) {
  try {
    await sendPasswordResetEmail(workspaceAuth(), String(email || '').trim(), {
      url: new URL('/login', window.location.origin).toString(),
    });
  } catch (error) {
    if (error?.code === 'auth/user-not-found') return { sent: true };
    const message = error?.code === 'auth/too-many-requests'
      ? 'Too many attempts. Wait a few minutes before trying again.'
      : error?.code === 'auth/invalid-email' ? 'Enter a valid email address.'
        : 'Password recovery is temporarily unavailable. Please try again.';
    throw new Error(message);
  }
  return { sent: true };
}

function googleProvider() {
  const google = new GoogleAuthProvider();
  google.addScope('email');
  google.addScope('profile');
  google.setCustomParameters({ prompt: 'select_account' });
  return google;
}

export async function sessionFromIdentityUser(user, { forceRefresh = false } = {}) {
  if (!user) return null;
  const tokenResult = await user.getIdTokenResult(forceRefresh);
  return {
    access_token: tokenResult.token,
    refresh_token: null,
    expires_at: Math.floor(new Date(tokenResult.expirationTime).getTime() / 1000),
    user: {
      id: user.uid,
      email: user.email || '',
      user_metadata: {
        name: user.displayName || '',
        full_name: user.displayName || '',
      },
      app_metadata: {},
    },
  };
}

export async function getWorkspaceIdentitySession({ forceRefresh = false } = {}) {
  const auth = workspaceAuth();
  try {
    await getRedirectResult(auth);
  } catch (error) {
    if (!auth.currentUser) {
      throw error;
    }
  }
  if (!auth.currentUser) {
    await auth.authStateReady();
  }
  return sessionFromIdentityUser(auth.currentUser, { forceRefresh });
}

export function onWorkspaceIdentityChange(callback) {
  return onIdTokenChanged(workspaceAuth(), async (user) => {
    callback(await sessionFromIdentityUser(user));
  });
}

export async function startWorkspaceGoogleSignIn() {
  await signInWithRedirect(workspaceAuth(), googleProvider());
}

export async function signInWithWorkspaceCustomToken(customToken) {
  const { user } = await signInWithCustomToken(workspaceAuth(), String(customToken || '').trim());
  return sessionFromIdentityUser(user);
}

export function identityAuthErrorMessage(error) {
  const code = String(error?.code || '').replace(/^auth\//, '');
  switch (code) {
    case 'invalid-email':
      return 'Enter a valid email address.';
    case 'missing-email':
    case 'missing-password':
      return 'Email and password are required.';
    case 'weak-password':
      return 'Password must be at least 6 characters.';
    case 'invalid-credential':
    case 'invalid-login-credentials':
    case 'wrong-password':
    case 'user-not-found':
      return 'Email or password is incorrect.';
    case 'too-many-requests':
      return 'Too many attempts. Try again later.';
    case 'email-not-verified':
      return 'Verify this email address before continuing.';
    default:
      return error?.message || 'Sign-in could not be completed. Please try again.';
  }
}

export async function signInWithWorkspaceEmail(email, password) {
  const auth = workspaceAuth();
  const { user } = await signInWithEmailAndPassword(auth, String(email || '').trim(), password);
  if (!user.emailVerified) {
    try {
      await sendEmailVerification(user, {
        url: `${window.location.origin}/login`,
        handleCodeInApp: false,
      });
    } catch {
      // The verification requirement still stands if delivery fails.
    }
    await signOut(auth);
    const unverified = new Error('Verify this email address before continuing.');
    unverified.code = 'auth/email-not-verified';
    throw unverified;
  }
  return sessionFromIdentityUser(user);
}

export async function signOutWorkspaceIdentity() {
  await signOut(workspaceAuth());
}

// Invitation sign-in precedes membership. Keep unverified identities here so
// recipients can verify their email without accidentally entering the app.
export async function signInForInvitation(email, password, createAccount = false) {
  const auth = workspaceAuth();
  const signIn = createAccount ? createUserWithEmailAndPassword : signInWithEmailAndPassword;
  const { user } = await signIn(auth, email.trim(), password);
  if (!user.emailVerified) await sendInvitationVerification();
  return { email: user.email, verified: user.emailVerified };
}

export async function sendInvitationVerification() {
  const user = workspaceAuth().currentUser;
  if (!user) throw new Error('Sign in before requesting a confirmation email.');
  await sendEmailVerification(user, { url: `${window.location.origin}/accept-invitation`, handleCodeInApp: false });
}

export async function invitationIdentity() {
  const auth = workspaceAuth();
  await auth.authStateReady();
  const user = auth.currentUser;
  if (!user) return null;
  await user.reload();
  return { email: user.email, verified: user.emailVerified, token: await user.getIdToken(true) };
}
